import { AddIcon } from "@/components/icons/action-icons";
import { useState } from "react";
import { formatDistanceToNow, format } from "date-fns";
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  History,
  Minus,
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  PAYROLL_AUDIT_ACTION_META,
  PAYROLL_AUDIT_FIELD_LABELS,
  PAYROLL_AUDIT_TONE_BADGE,
  PAYROLL_LINE_TYPE_LABELS,
} from "@/utils/enums/payroll.enum";
import { useGetItemAuditLogsQuery } from "@/services/(company)/payrollRunsApi";
import type {
  IPayrollAuditLog,
  IPayrollAuditLogChanges,
} from "@/types/(company)/payrollRuns.interface";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

interface IProps {
  runId: number;
  itemId: number;
}

const humanizeField = (key: string): string => {
  if (PAYROLL_AUDIT_FIELD_LABELS[key]) return PAYROLL_AUDIT_FIELD_LABELS[key];
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const fullName = (u: IPayrollAuditLog["user"], translate: TFunction) => {
  if (!u) return "System";
  return (
    `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() ||
    translate("company:payrolls.unknown")
  );
};

const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((s) => s[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";

const fmtValue = (v: unknown): string => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") {
    return v.toLocaleString(undefined, {
      minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
      maximumFractionDigits: 2,
    });
  }
  return String(v);
};

const fmtAmount = (n: number) =>
  n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const lineTypeLabel = (t: string) =>
  PAYROLL_LINE_TYPE_LABELS[t] ??
  t.charAt(0).toUpperCase() + t.slice(1).toLowerCase().replace(/_/g, " ");

const FieldChangeRow = ({
  label,
  from,
  to,
}: {
  label: string;
  from: unknown;
  to: unknown;
}) => (
  <div className="flex items-center gap-3 py-1.5 text-xs">
    <div className="shrink-0 w-5 h-5 rounded-full bg-info-muted border border-blue-200 flex items-center justify-center">
      <ArrowRight size={11} className="text-info" />
    </div>
    <div className="flex-1 min-w-0">
      <span className="text-gray-700 font-medium">{label}</span>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <span className="text-gray-400 line-through">{fmtValue(from)}</span>
      <ArrowRight size={11} className="text-gray-400" />
      <span className="text-gray-900 font-semibold">{fmtValue(to)}</span>
    </div>
  </div>
);

const LineAddedRow = ({ name, amount }: { name: string; amount: number }) => {
  const { t: translate } = useTranslation(["company", "common"]);
  return (
    <div className="flex items-center gap-3 py-1.5 text-xs">
      <div className="shrink-0 w-5 h-5 rounded-full bg-green-50 border border-green-200 flex items-center justify-center">
        <AddIcon size={11} className="text-green-600" />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-gray-700">{name}</span>
        <span className="ms-1.5 text-[10px] text-green-700 font-medium uppercase tracking-wide">
          {translate("company:payrolls.added")}
        </span>
      </div>
      {/* eslint-disable-next-line i18next/no-literal-string -- sign and ISO currency code, not copy */}
      <span className="text-green-700 font-semibold shrink-0">
        +AED {fmtAmount(amount)}
      </span>
    </div>
  );
};

const LineRemovedRow = ({ name, amount }: { name: string; amount: number }) => {
  const { t: translate } = useTranslation(["company", "common"]);
  return (
    <div className="flex items-center gap-3 py-1.5 text-xs">
      <div className="shrink-0 w-5 h-5 rounded-full bg-red-50 border border-red-200 flex items-center justify-center">
        <Minus size={11} className="text-red-600" />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-gray-500 line-through">{name}</span>
        <span className="ms-1.5 text-[10px] text-red-700 font-medium uppercase tracking-wide">
          {translate("my-space:toasts.removed")}
        </span>
      </div>
      {/* eslint-disable-next-line i18next/no-literal-string -- sign and ISO currency code, not copy */}
      <span className="text-red-700 font-semibold shrink-0">
        −AED {fmtAmount(amount)}
      </span>
    </div>
  );
};

const LineModifiedRow = ({
  name,
  fromAmt,
  toAmt,
}: {
  name: string;
  fromAmt: number;
  toAmt: number;
}) => {
  const { t: translate } = useTranslation(["company", "common"]);
  const delta = toAmt - fromAmt;
  return (
    <div className="flex items-center gap-3 py-1.5 text-xs">
      <div className="shrink-0 w-5 h-5 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
        <ArrowRight size={11} className="text-amber-600" />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-gray-700 font-medium">{name}</span>
        <span className="ms-1.5 text-[10px] text-amber-700 font-medium uppercase tracking-wide">
          {translate("company:payrolls.modified")}
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-gray-400 line-through">
          AED {fmtAmount(fromAmt)}
        </span>
        <ArrowRight size={11} className="text-gray-400" />
        <span className="text-gray-900 font-semibold">
          AED {fmtAmount(toAmt)}
        </span>
        {delta !== 0 && (
          <span
            className={`text-[10px] font-semibold ${
              delta > 0 ? "text-green-600" : "text-red-600"
            }`}
          >
            ({delta > 0 ? "+" : ""}
            {fmtAmount(delta)})
          </span>
        )}
      </div>
    </div>
  );
};

const SectionHeader = ({ title, count }: { title: string; count: number }) => (
  <div className="flex items-center gap-2 mt-3 first:mt-0 mb-1">
    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
      {title}
    </span>
    <span className="text-[10px] text-gray-400">({count})</span>
    <div className="flex-1 border-t border-gray-100" />
  </div>
);

const renderChanges = (
  changes: IPayrollAuditLogChanges,
  translate: TFunction,
) => {
  const byLineType = new Map<
    string,
    {
      added: typeof changes.linesAdded;
      modified: typeof changes.linesModified;
      removed: typeof changes.linesRemoved;
    }
  >();

  const ensure = (lineType: string) => {
    if (!byLineType.has(lineType)) {
      byLineType.set(lineType, { added: [], modified: [], removed: [] });
    }
    return byLineType.get(lineType)!;
  };

  for (const line of changes.linesAdded ?? [])
    ensure(line.lineType).added!.push(line);
  for (const line of changes.linesModified ?? [])
    ensure(line.lineType).modified!.push(line);
  for (const line of changes.linesRemoved ?? [])
    ensure(line.lineType).removed!.push(line);

  const fieldEntries = changes.fields ? Object.entries(changes.fields) : [];
  const sortedTypes = Array.from(byLineType.keys()).sort();

  const hasAny =
    fieldEntries.length > 0 || sortedTypes.length > 0 || Boolean(changes.note);

  if (!hasAny) return null;

  return (
    <div className="mt-2 bg-gray-50/50 rounded-xs px-3 py-2 border border-gray-100">
      {fieldEntries.length > 0 && (
        <>
          <SectionHeader
            title={translate("company:payrolls.field_changes")}
            count={fieldEntries.length}
          />
          {fieldEntries.map(([field, diff]) => (
            <FieldChangeRow
              key={`f-${field}`}
              label={humanizeField(field)}
              from={diff.from}
              to={diff.to}
            />
          ))}
        </>
      )}

      {sortedTypes.map((lineType) => {
        const group = byLineType.get(lineType)!;
        const total =
          (group.added?.length ?? 0) +
          (group.modified?.length ?? 0) +
          (group.removed?.length ?? 0);
        return (
          <div key={`group-${lineType}`}>
            <SectionHeader
              title={`${lineTypeLabel(lineType)}s`}
              count={total}
            />
            {group.added?.map((line) => (
              <LineAddedRow
                key={`a-${lineType}-${line.id ?? line.name}`}
                name={line.name}
                amount={line.amount}
              />
            ))}
            {group.modified?.map((line) => (
              <LineModifiedRow
                key={`m-${lineType}-${line.id}`}
                name={line.name}
                fromAmt={line.from.amount}
                toAmt={line.to.amount}
              />
            ))}
            {group.removed?.map((line) => (
              <LineRemovedRow
                key={`r-${lineType}-${line.id}`}
                name={line.name}
                amount={line.amount}
              />
            ))}
          </div>
        );
      })}

      {changes.note && (
        <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-600">
          <span className="font-medium text-gray-700">
            {translate("company:payrolls.reason")}
          </span>
          <span className="italic">{changes.note}</span>
        </div>
      )}
    </div>
  );
};

const AuditEntry = ({ log }: { log: IPayrollAuditLog }) => {
  const { t: translate } = useTranslation(["company", "common"]);
  const meta = PAYROLL_AUDIT_ACTION_META[log.action] ?? {
    label: log.action,
    tone: "neutral" as const,
    Icon: History,
  };
  const userName = fullName(log.user, translate);
  const userAvatar = log.user?.profileImage ?? undefined;
  const changesBlock = renderChanges(log.changes ?? {}, translate);

  let relative = "";
  let absolute = "";
  try {
    relative = formatDistanceToNow(new Date(log.createdAt), {
      addSuffix: true,
    });
    absolute = format(new Date(log.createdAt), "dd MMM yyyy, HH:mm");
  } catch {
    relative = log.createdAt;
  }

  const Icon = meta.Icon;

  return (
    <div className="flex gap-3 py-4 border-b border-gray-100 last:border-0">
      <Avatar className="h-9 w-9 shrink-0">
        <AvatarImage src={userAvatar} />
        <AvatarFallback className="text-xs bg-gray-100 text-gray-600">
          {initials(userName)}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-900">{userName}</span>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-xs border text-[11px] font-medium ${PAYROLL_AUDIT_TONE_BADGE[meta.tone]}`}
          >
            <Icon size={11} />
            {meta.label}
          </span>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs text-gray-400 cursor-help">
                  {relative}
                </span>
              </TooltipTrigger>
              <TooltipContent>{absolute}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {changesBlock}
      </div>
    </div>
  );
};

export const ChangeHistory = ({ runId, itemId }: IProps) => {
  const { t: translate } = useTranslation(["company", "common"]);
  const [expanded, setExpanded] = useState(true);
  const { data, isLoading, isFetching } = useGetItemAuditLogsQuery(
    { runId, itemId },
    { skip: !runId || !itemId },
  );

  const logs = data?.data ?? [];

  return (
    <section className="rounded-xs border border-border bg-white">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 border-b border-border"
      >
        <div className="flex items-center gap-2">
          <History size={16} className="text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900">
            {translate("company:payrolls.change_history")}
          </h3>
          {logs.length > 0 && (
            <span className="text-xs text-gray-400">({logs.length})</span>
          )}
        </div>
        {expanded ? (
          <ChevronUp size={16} className="text-gray-400" />
        ) : (
          <ChevronDown size={16} className="text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="px-4">
          {(isLoading || isFetching) && logs.length === 0 ? (
            <div className="py-6 text-center text-xs text-gray-400">
              {translate("company:payrolls.loading_history")}
            </div>
          ) : logs.length === 0 ? (
            <div className="py-6 text-center text-xs text-gray-400">
              {translate("company:payrolls.no_changes_recorded_yet")}
            </div>
          ) : (
            <div>
              {logs.map((log) => (
                <AuditEntry key={log.id} log={log} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
